import type { IconLookup, IconName, IconPrefix } from "@fortawesome/fontawesome-common-types";

import { kebabCase } from "@portal/app/_common/lib/changeCase";

/**
 * Resolves an icon name string into a FontAwesome {@link IconLookup}.
 *
 * Supported formats:
 *  - `"faLaptop"` / `"faChalkboardTeacher"` — legacy camelCase (strips leading `fa`, kebab-cases the rest, defaults to `fas`)
 *  - `"fas:laptop"` / `"far:heart"` — explicit prefix:name
 *  - `"laptop"` — bare kebab-case name (defaults to `fas`)
 *
 * Returns `null` for empty / whitespace-only input.
 */
export function resolveIconName(raw: string | null | undefined): IconLookup | null {
  if (!raw || !raw.trim()) return null;

  const trimmed = raw.trim();

  // Explicit prefix format: "fas:laptop", "far:heart"
  if (trimmed.includes(":")) {
    const [prefix, ...rest] = trimmed.split(":");
    const iconName = rest.join(":"); // handle unlikely extra colons
    if (prefix && iconName) {
      return { prefix: prefix as IconPrefix, iconName: iconName as IconName };
    }
  }

  // Legacy camelCase: "faLaptop" → strip "fa" prefix, kebab-case remainder
  if (/^fa[A-Z]/.test(trimmed)) {
    const withoutFa = trimmed.slice(2); // "Laptop", "ChalkboardTeacher"
    return { prefix: "fas", iconName: kebabCase(withoutFa) as IconName };
  }

  // Bare name: "laptop", "graduation-cap"
  return { prefix: "fas", iconName: trimmed as IconName };
}
